/**
 * Fate character-sheet reference data (Phase 3).
 *
 * Source of truth: "Fate v0.04 - Türkçe Playtest" — skill names & ladder
 * labels are verbatim from the rulebook PDF + the scanned Turkish character
 * sheet (karakter kağıdı.png). Reconciliation with
 * docs/phase3-charcreation-rules.md is the acceptance step.
 */

/** The 19 skills, sheet/alphabetical order. */
export const FATE_SKILLS: readonly string[] = [
  'Akademik',
  'Aldatmaca',
  'Atıcılık',
  'Bağlantılar',
  'Beden',
  'Çeviklik',
  'Empati',
  'Farkındalık',
  'Gizlilik',
  'Gözlem',
  'Hırsızlık',
  'İlim',
  'İrade',
  'Kaynaklar',
  'Kışkırtma',
  'Muhabbet',
  'Sürücülük',
  'Yakın Dövüş',
  'Zanaat',
] as const;

export interface LadderStep {
  value: number;
  label: string;
}

/**
 * Adjective ladder, low → high — all 13 rungs, verbatim transcription of the
 * SEVİYE/SIFAT table from docs/phase3-charcreation-rules.md §B.4 (the ladder
 * exists only as an image in the source, printed 6 = PDF 11; A-25).
 *
 * Note: the +4 rung is "Harika" on the ladder but "Müthiş" in the stress
 * table (A-16) — the ladder spelling wins here, both are flagged upstream.
 */
export const FATE_LADDER: readonly LadderStep[] = [
  { value: -4, label: 'Felaket' },
  { value: -3, label: 'Rezalet' },
  { value: -2, label: 'Kötü' },
  { value: -1, label: 'Dandik' },
  { value: 0, label: 'Düz' },
  { value: 1, label: 'Eh' },
  { value: 2, label: 'İyi' },
  { value: 3, label: 'Baya İyi' },
  { value: 4, label: 'Harika' },
  { value: 5, label: 'Muhteşem' },
  { value: 6, label: 'Şahane' },
  { value: 7, label: 'İnanılmaz' },
  { value: 8, label: 'Efsane' },
] as const;

/**
 * Starting distribution from the character sheet:
 * "Bir tane Harika (+4), iki tane Baya İyi (+3), üç taneli (+2),
 *  dört tane Eh (+1) ile başlayın, Düz (+0)'da dinlenin."
 */
export const FATE_STANDARD_DISTRIBUTION: Readonly<Record<number, number>> = {
  4: 1,
  3: 2,
  2: 3,
  1: 4,
} as const;

/** Character sheet rule: "Stunt + refresh = 6 olmalı." */
export const FATE_STUNT_REFRESH_TOTAL = 6;

/** Starting stunts per the sheet ("3 stunt yazarak başlayın"). */
export const FATE_STARTING_STUNTS = 3;

export function ladderLabel(value: number): string {
  const step = FATE_LADDER.find((s) => s.value === value);
  return step ? step.label : String(value);
}

/**
 * Stress track size, Fate CS p.12 (BEDEN / İRADE → FİZİKSEL / ZİHİNSEL STRESS):
 *
 *   Düz (+0) ................. 3 boxes
 *   Eh (+1) / İyi (+2) ....... 4 boxes
 *   Baya İyi (+3) / (+4) ..... 6 boxes
 *   Muhteşem (+5) and up ..... 6 boxes + an extra moderate consequence slot
 *
 * Everyone owns at least three boxes; `extraModerate` is the only part of the
 * table that changes the consequence row rather than the stress row.
 */
export const FATE_BASE_STRESS_BOXES = 3;
export const FATE_MAX_STRESS_BOXES = 6;

export function stressBoxes(rank: number): { boxes: number; extraModerate: boolean } {
  if (rank >= 5) return { boxes: 6, extraModerate: true };
  if (rank >= 3) return { boxes: 6, extraModerate: false };
  if (rank >= 1) return { boxes: 4, extraModerate: false };
  return { boxes: FATE_BASE_STRESS_BOXES, extraModerate: false };
}
